import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, LinkIcon, UnlinkIcon } from '@utils/icons';
import type { ISite } from '@interfaces/IProjectMerge';
import { Button } from '@components/ui/Button/Button';
import { ProvBadge } from '@components/ui/ProvBadge/ProvBadge';
import { formatJstShort } from '@utils/dateUtils';
import styles from './ExistingLinks.module.css';

/** A D-ORCA site plus the resolved display names for each upstream (falls back
    to the raw code when the upstream row is no longer in the picker list). */
export interface IResolvedSite {
  site: ISite;
  genbaName: string;
  trusstorName: string;
}

interface IExistingLinksProps {
  sites: IResolvedSite[];
  open: boolean;
  onToggle: () => void;
  onDelete: (site: ISite) => void;
}

/** Collapsible "Your sites" section listing every D-ORCA-owned site. */
export function ExistingLinks({ sites, open, onToggle, onDelete }: IExistingLinksProps) {
  const { t } = useTranslation();
  return (
    <section className={styles.section}>
      <button type="button" className={styles.head} onClick={onToggle} aria-expanded={open}>
        <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`}>
          <ChevronRightIcon size={16} />
        </span>
        <h3 className={styles.title}>{t('projectMerge.existingLinks.title')}</h3>
        <span className={styles.count}>{sites.length}</span>
        <span className={styles.spacer} />
        <span className={styles.hint}>{t('projectMerge.existingLinks.permanentNote')}</span>
      </button>

      {open ? (
        <div className={styles.body}>
          {sites.length === 0 ? (
            <div className={styles.empty}>{t('projectMerge.existingLinks.empty')}</div>
          ) : null}
          {sites.map(({ site, genbaName, trusstorName }) => (
            <div className={styles.row} key={site.siteId}>
              <div className={styles.end}>
                <ProvBadge provider="genba" />
                <span className={styles.info}>
                  <span className={`${styles.nm} ${styles.nmJp}`}>{genbaName}</span>
                  <span className={styles.cd}>{site.genbaSiteNumber}</span>
                </span>
              </div>

              <div className={styles.mid}>
                <span className={`${styles.nm} ${styles.nmJp} ${styles.siteName}`}>{site.name}</span>
                <span className={styles.chain}>
                  <LinkIcon size={15} />
                </span>
                <span className={styles.sid}>{site.siteId}</span>
                <span className={styles.when}>
                  {t('projectMerge.existingLinks.linkedMeta', { at: formatJstShort(site.createdAt) })}
                </span>
              </div>

              <div className={`${styles.end} ${styles.endRight}`}>
                <ProvBadge provider="trusstor" />
                <span className={styles.info}>
                  <span className={styles.nm}>{trusstorName}</span>
                  <span className={styles.cd}>{site.trusstorProjectId}</span>
                </span>
              </div>

              <div className={styles.actions}>
                <Button variant="danger" size="sm" onClick={() => onDelete(site)}>
                  <UnlinkIcon size={14} />
                  {t('projectMerge.existingLinks.unlink')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
